import { CHAMPS_FICHE } from '@repere/core';

/**
 * Comment chaque champ de la fiche se renseigne, et sous quel intertitre.
 *
 * Ce fichier vit dans l'application et non dans `@repere/core` : le noyau décide **quelles
 * données** Repère collecte — c'est sa liste fermée, et c'est la mesure de minimisation —
 * tandis qu'un pas-à-pas ou une rangée de pastilles relève de l'écran. Un serveur n'a pas
 * à savoir qu'une pointure se saisit avec deux boutons.
 *
 * Les dix champs partageaient la même zone de texte libre. Chacun a pourtant un espace de
 * réponse connu — huit groupes sanguins, une pointure entière, une liste de personnes — et
 * la zone de texte était la seule saisie qui n'en tirait jamais parti : « O+ », « O
 * positif » et « 0+ » y entraient tous les trois, dans un champ qu'on relit aux urgences.
 *
 * Un champ ajouté au noyau sans entrée ici fait échouer un test. C'est voulu : le moment
 * d'ajouter un champ est le bon moment pour décider comment il se remplit.
 */

export type GroupeFiche = 'tailles' | 'sante' | 'ecole';

export const GROUPES: readonly { cle: GroupeFiche; titre: string }[] = [
  { cle: 'tailles', titre: 'Tailles' },
  { cle: 'sante', titre: 'Santé' },
  { cle: 'ecole', titre: 'École et vie quotidienne' },
];

export type Saisie =
  /** Un choix dans une liste courte et fermée, plus une valeur libre pour le reste. */
  | { type: 'pastilles'; options: readonly string[]; libre: boolean }
  /** Un entier qu'on ajuste d'un cran. */
  | { type: 'compteur'; min: number; max: number; defaut: number }
  /** Une liste d'éléments courts, ajoutés et retirés un à un. */
  | { type: 'etiquettes'; exemple: string }
  /** Une liste de lignes à plusieurs colonnes. */
  | { type: 'rangs'; colonnes: readonly string[]; ajouter: string }
  /** Une ligne de texte, quand la réponse est un nom propre. */
  | { type: 'texte' };

export interface Presentation {
  groupe: GroupeFiche;
  saisie: Saisie;
}

export const PRESENTATION: Record<string, Presentation> = {
  // Les âges couvrent l'enfance ; les centimètres suivent parce que les deux systèmes
  // cohabitent sur les étiquettes des vêtements. `libre` garde la porte ouverte au reste.
  taille_vetements: {
    groupe: 'tailles',
    saisie: {
      type: 'pastilles',
      options: ['2 ans', '4 ans', '6 ans', '8 ans', '10 ans', '12 ans', '14 ans', '16 ans'],
      libre: true,
    },
  },
  // Bornes réelles d'une pointure d'enfant puis d'adulte. Un cran à la fois : c'est le
  // geste que la commande doit coûter, parce que c'est le pas de la réalité.
  pointure: { groupe: 'tailles', saisie: { type: 'compteur', min: 16, max: 48, defaut: 30 } },

  allergies: { groupe: 'sante', saisie: { type: 'etiquettes', exemple: 'Arachides' } },
  traitements: {
    groupe: 'sante',
    saisie: {
      type: 'rangs',
      colonnes: ['Nom', 'Posologie', 'Durée'],
      ajouter: 'Ajouter un traitement',
    },
  },
  // Le téléphone isolé de son nom : c'est ce qu'on cherche à faire en ouvrant ce champ.
  medecin_traitant: {
    groupe: 'sante',
    saisie: { type: 'rangs', colonnes: ['Nom', 'Téléphone'], ajouter: 'Ajouter un médecin' },
  },
  // Les huit valeurs possibles, et rien d'autre : le zéro et la lettre O se confondent à
  // la saisie, et pas dans une liste de boutons.
  groupe_sanguin: {
    groupe: 'sante',
    saisie: {
      type: 'pastilles',
      options: ['A+', 'A−', 'B+', 'B−', 'AB+', 'AB−', 'O+', 'O−'],
      libre: false,
    },
  },

  ecole: { groupe: 'ecole', saisie: { type: 'texte' } },
  code_postal_ecole: { groupe: 'ecole', saisie: { type: 'texte' } },
  enseignant: { groupe: 'ecole', saisie: { type: 'texte' } },
  personnes_autorisees: {
    groupe: 'ecole',
    saisie: {
      type: 'rangs',
      colonnes: ['Prénom et nom', 'Lien avec l’enfant'],
      ajouter: 'Ajouter une personne',
    },
  },
  activites: {
    groupe: 'ecole',
    saisie: {
      type: 'rangs',
      colonnes: ['Activité', 'Jour', 'Horaire'],
      ajouter: 'Ajouter une activité',
    },
  },
};

/** Les champs d'un groupe, dans l'ordre du noyau. */
export function champsDuGroupe(groupe: GroupeFiche) {
  return CHAMPS_FICHE.filter((champ) => PRESENTATION[champ.cle]?.groupe === groupe);
}
